import React, {
    useContext, useState, useCallback, useMemo, useEffect
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { pageTemplateProps } from '../prop-types/wizardPropTypes';
import styles from './SgWizardPageTemplate.module.scss';
import messages from './SgWizardPageTemplate.messages';
import SgBreadcrumbs from '../components/sg-breadcrumbs/SgBreadcrumbs';

function renderLoader(isLoading) {
    return isLoading ? <div id="wizardLoader" className="sgAnimatedLoader" /> : null;
}

function SgWizardPageTemplate(props) {
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isPhone = breakpoint === 'phone';
    const [showSidebar, setShowSidebar] = useState(false);
    const {
        children: content,
        onNext,
        isLoadingNext,
        disableNext,
        showNext,
        onPrevious,
        isLoadingPrevious,
        disablePrevious,
        showPrevious,
        previousLabel,
        continueButtonLabel,
        isLoadingCancel,
        showCancel,
        renderContextComponent,
        wizardSnapshot,
        showContextComponent,
        showWizardHeader,
        showFooter,
        footerMessages,
        continueButtonId,
        backButtonId,
        disableBreadcrumbs,
        additionalNextContent,
        stackButtonsVertically,
        customButtonsContainerClassName
    } = props;

    useEffect(() => {
        switch (breakpoint) {
            case 'phone':
                setShowSidebar(false);
                break;
            default:
                setShowSidebar(true);
                break;
        }
    }, [breakpoint]);

    const contextComponent = useCallback(() => {
        return <div className={styles.wizardContext}>{renderContextComponent(wizardSnapshot)}</div>;
    }, [renderContextComponent, wizardSnapshot]);

    const shouldShowButtons = useMemo(() => !(
        showNext === false
        && showPrevious === false
        && showCancel === false
    ), [showCancel, showNext, showPrevious]);

    const isPerformingTransiton = useMemo(() => (
        isLoadingCancel || isLoadingPrevious || isLoadingNext
    ), [isLoadingCancel, isLoadingNext, isLoadingPrevious]);

    const wizardClassName = classNames(styles.wizardMain, {
        [styles.wizardMainShowSidebar]: showSidebar && isPhone
    });

    const nextButton = useMemo(() => (
        isLoadingNext
            ? (
                renderLoader(isLoadingNext)
            )
            : (
                <Button
                    id={continueButtonId}
                    className={`${styles.nextbutton} sgTransactionButton`}
                    disabled={disableNext || isPerformingTransiton}
                    onClick={onNext}
                >
                    { continueButtonLabel ? translator(continueButtonLabel) : translator(messages.continue)}
                </Button>
            )
    ), [disableNext, isLoadingNext, isPerformingTransiton, onNext, translator]);

    const buttonContainerClassName = classNames(styles.navTransitionButtons, {
        [styles.navTransitionButtonOnlyNext]: !showPrevious && isPhone
    });

    return (
        <div className={styles.digitalWizard}>
            {showContextComponent && contextComponent()}
            <div className={`${wizardClassName} sgWizardMainLayout`}>
                {!isPhone || showSidebar ? (
                    <div className={styles.wizardNavigation}>
                        <SgBreadcrumbs disableBreadcrumbs={disableBreadcrumbs} />
                    </div>
                ) : null}
                <div id="mainContent" className={styles.wizardContent}>
                    {showWizardHeader && contextComponent()}
                    {content}
                    <div className={classNames({ [styles.navButtons]: shouldShowButtons })}>
                        {/* eslint-disable-next-line max-len */}
                        <div className={`${customButtonsContainerClassName} ${buttonContainerClassName} ${showPrevious || showNext ? styles.navButtonsDisplayed : ''}`}>
                            {stackButtonsVertically && (
                                <div className="sgButtonAndText">
                                    {showNext && nextButton}
                                    {showNext && !!additionalNextContent && additionalNextContent}
                                </div>
                            )}
                            {showPrevious && (
                                <Button
                                    id={backButtonId}
                                    disabled={disablePrevious || isPerformingTransiton}
                                    type="outlined"
                                    onClick={onPrevious}
                                    className="sgSecondaryBlueButton"
                                >
                                    {translator(previousLabel)}
                                    {renderLoader(isLoadingPrevious)}
                                </Button>
                            )}
                            {!stackButtonsVertically && (
                                <>
                                    {showNext && !!additionalNextContent && additionalNextContent}
                                    {showNext && nextButton}
                                </>
                            )}
                        </div>
                    </div>
                    {showFooter && (
                        <div className="sgFooterContainer">
                            {footerMessages()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

SgWizardPageTemplate.propTypes = {
    ...pageTemplateProps,
    /** a function that accepts wizardData to render a React component */
    renderContextComponent: PropTypes.func,
    /** a function that accepts wizardData to render a React component */
    showContextComponent: PropTypes.bool,
    showWizardHeader: PropTypes.bool,
    continueButtonId: PropTypes.string,
    backButtonId: PropTypes.string
};

SgWizardPageTemplate.defaultProps = {
    renderContextComponent: () => null,
    showContextComponent: true,
    showWizardHeader: false,
    continueButtonId: 'gw-wizard-next',
    backButtonId: 'gw-wizard-previous'
};

export default SgWizardPageTemplate;
